import { useEffect, useState } from 'react';

export default function MouseFollower() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [glitchTrails, setGlitchTrails] = useState<Array<{x: number, y: number, id: string, opacity: number}>>([]);
  const [isMoving, setIsMoving] = useState(false);

  useEffect(() => {
    let moveTimeout: NodeJS.Timeout;
    
    const handleMouseMove = (e: MouseEvent) => {
      const newPosition = { x: e.clientX, y: e.clientY };
      setMousePosition(newPosition);
      setIsMoving(true);
      
      // Add glitch trail
      const trail = {
        x: newPosition.x + (Math.random() - 0.5) * 20,
        y: newPosition.y + (Math.random() - 0.5) * 20,
        id: Math.random().toString(36),
        opacity: 1
      };
      
      setGlitchTrails(prev => [...prev.slice(-15), trail]);
      
      clearTimeout(moveTimeout);
      moveTimeout = setTimeout(() => setIsMoving(false), 100);
    };

    window.addEventListener('mousemove', handleMouseMove);
    
    // Fade out trails
    const trailInterval = setInterval(() => {
      setGlitchTrails(prev => 
        prev.map(trail => ({ ...trail, opacity: trail.opacity * 0.85 }))
           .filter(trail => trail.opacity > 0.01)
      );
    }, 50);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(moveTimeout);
      clearInterval(trailInterval);
    };
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {/* Main cursor glow */}
      <div
        className={`absolute w-8 h-8 rounded-full transition-all duration-100 ${
          isMoving ? 'animate-pulse' : ''
        }`}
        style={{
          left: mousePosition.x - 16,
          top: mousePosition.y - 16,
          background: 'radial-gradient(circle, rgba(0, 255, 255, 0.6) 0%, rgba(255, 0, 255, 0.4) 50%, transparent 70%)',
          boxShadow: `
            0 0 20px rgba(0, 255, 255, 0.8),
            0 0 40px rgba(255, 0, 255, 0.6),
            0 0 80px rgba(0, 255, 0, 0.3)
          `,
          filter: isMoving ? 'blur(1px)' : 'blur(0px)'
        }}
      />
      
      {/* Cursor core */}
      <div
        className="absolute w-2 h-2 bg-white rounded-full mix-blend-difference"
        style={{
          left: mousePosition.x - 4,
          top: mousePosition.y - 4,
        }}
      />
      
      {/* Glitch trails */}
      {glitchTrails.map((trail) => (
        <div
          key={trail.id}
          className="absolute w-4 h-4 rounded-full animate-pulse"
          style={{
            left: trail.x - 8,
            top: trail.y - 8,
            opacity: trail.opacity,
            background: `radial-gradient(circle, 
              rgba(${Math.random() > 0.5 ? '255, 0, 255' : '0, 255, 255'}, ${trail.opacity}) 0%, 
              transparent 70%
            )`,
            transform: `scale(${0.5 + trail.opacity * 0.5}) rotate(${Math.random() * 360}deg)`,
            filter: `blur(${(1 - trail.opacity) * 2}px)`,
          }}
        />
      ))}
      
      {/* Electric arcs (appear randomly when moving) */}
      {isMoving && Math.random() > 0.7 && (
        <div
          className="absolute pointer-events-none"
          style={{
            left: mousePosition.x,
            top: mousePosition.y,
            width: '100px',
            height: '100px',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <svg width="100" height="100" className="animate-pulse">
            <path
              d={`M 50 50 Q ${20 + Math.random() * 60} ${20 + Math.random() * 60} ${80 + Math.random() * 20} ${80 + Math.random() * 20}`}
              stroke="rgba(0, 255, 255, 0.8)"
              strokeWidth="1"
              fill="none"
              className="animate-pulse"
              style={{
                filter: 'drop-shadow(0 0 3px rgba(0, 255, 255, 0.8))'
              }}
            />
          </svg>
        </div>
      )}
    </div>
  );
}